import { computed, toValue } from 'vue'

import { usePageStore } from '../stores/page'
import { ucFirst } from '../utils/strings'

import type { MaybeRefOrGetter } from 'vue'
import type { ComposedParagraphTeaser } from '@xcel-next/composer-schema'
import type { UnifiedProduct } from './use-product-group'

type StructuredDataOffer = {
  '@type': 'Offer',
  url: string,
  priceCurrency: 'EUR',
  availability?: 'InStock' | 'OutOfStock',
  price?: string
}

type StructuredDataReview = {
  '@type': 'Review',
  reviewRating: {
    '@type': 'Rating',
    ratingValue: string,
    bestRating: '5'
  },
  author?: StructuredDataAuthor | StructuredDataAuthor[]
}

type StructuredDataAuthor = {
  '@type': 'Person',
  name?: string,
  url?: string
}

type StructuredDataProperty = {
  '@type': 'PropertyValue',
  name?: string,
  value?: string
}

type UseProductStructuredDataOptions = {
  product: MaybeRefOrGetter<UnifiedProduct | undefined>,
  isContentCommerce?: MaybeRefOrGetter<boolean | undefined>,
  isPreview?: MaybeRefOrGetter<boolean | undefined>
}

export function useProductStructuredData (options: UseProductStructuredDataOptions) {
  const pageStore = usePageStore()

  // validates and extracts author data, returns empty array if all failed
  function getAuthors (author: ComposedParagraphTeaser | ComposedParagraphTeaser[] | undefined): StructuredDataAuthor[] {
    return (Array.isArray(author) ? author : [author])
      .filter((a): a is ComposedParagraphTeaser => !!(a?.titleLong && a?.url))
      .map(a => ({ '@type': 'Person', name: a.titleLong, url: pageStore.pageData?.pageMeta?.baseUrl + a.url }))
  }

  const structuredDataOffer = computed<StructuredDataOffer | undefined>(() => {
    const { link: { url } = {}, sellingPrice, lastKnownPrice } = toValue(options.product) ?? {}
    if (!url || (!sellingPrice && !lastKnownPrice)) return undefined

    const offer: StructuredDataOffer = {
      '@type': 'Offer',
      url,
      priceCurrency: 'EUR'
    }

    if (sellingPrice) {
      offer.availability = 'InStock'
      offer.price = (sellingPrice / 100).toFixed(2)
    } else if (lastKnownPrice) {
      offer.availability = 'OutOfStock'
      offer.price = (lastKnownPrice / 100).toFixed(2)
    }

    return offer
  })

  const structuredDataReview = computed<StructuredDataReview | undefined>(() => {
    const editorialStarRating = toValue(options.product)?.editorialStarRating
    if (!editorialStarRating) return undefined

    return {
      '@type': 'Review',
      reviewRating: {
        '@type': 'Rating',
        ratingValue: `${editorialStarRating}`,
        bestRating: '5'
      }
    }
  })

  const structuredDataAuthors = computed<StructuredDataAuthor | StructuredDataAuthor[] | undefined>(() => {
    const authors = [
      ...getAuthors(pageStore.pageData?.body?.author),
      ...getAuthors(pageStore.pageData?.body?.additionalAuthors)
    ]

    if (authors.length > 1) return authors // if there are more than one authors, we create an array stucture
    if (authors.length === 1) return authors[0] // if there's only one author, we create a single object

    return undefined
  })

  const structuredDataProsAndCons = computed(() => {
    const prosAndCons = toValue(options.product)?.prosAndCons
    const getItems = (type: 'pros' | 'cons'): StructuredDataProperty[] => (
      prosAndCons?.[type]?.map((value) => ({
        '@type': 'PropertyValue',
        name: ucFirst(type),
        value: value?.replaceAll('<p>', '').replaceAll('</p>', '').trim()
      })) ?? []
    )

    return {
      pros: getItems('pros'),
      cons: getItems('cons')
    }
  })

  const structuredDataTechnicalData = computed<StructuredDataProperty[]>(() =>
    toValue(options.product)?.technicalData?.map(({ key: name, value }) => ({
      '@type': 'PropertyValue',
      name,
      value
    })) ?? []
  )

  // only return/render the product structured data if we have an offer (with a price) or a review (from editorial star rating)
  // you can omit either if the other exists, but not both
  // ps `aggregatedRating` is also allowed, alas we do not have one for products
  // see the the google guidelines: https://developers.google.com/search/docs/appearance/structured-data/product
  const hasStructuredData = computed(() => {
    const product = toValue(options.product)
    const hasPrice = !!product?.sellingPrice || !!product?.lastKnownPrice
    const hasReview = !!product?.editorialStarRating

    return !!product && !!toValue(options.isContentCommerce) && (hasPrice || hasReview) && !toValue(options.isPreview)
  })

  const structuredData = computed(() => {
    const product = toValue(options.product)
    const { title: name, image } = product ?? {}
    const { pros, cons } = structuredDataProsAndCons.value

    const json: Record<string, unknown> = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name,
      image: [image],
      description: undefined, // UnifiedProduct has no description field - kept for schema.org compatibility, same as before this refactor
      offers: structuredDataOffer.value,
      review: structuredDataReview.value,
      additionalProperty: [...pros, ...cons, ...structuredDataTechnicalData.value]
    }

    // note only available in the RS - irrelevant for the preview
    if (structuredDataAuthors.value && json.review) {
      (json.review as StructuredDataReview).author = structuredDataAuthors.value
    }

    return `<script type="application/ld+json">${JSON.stringify(json)}<\/script>` // eslint-disable-line no-useless-escape
  })

  return {
    hasStructuredData,
    structuredData
  }
}
